import { Component, CUSTOM_ELEMENTS_SCHEMA, forwardRef } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store, Select } from '@ngxs/store';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
    AlertService,
    ButtonColors,
    ButtonModule,
    NumberfieldModule,
    TextfieldModule,
} from '@ed---interne/ng-uui-components';
import { BusinessActivityModel, ProjectStatus } from '../../project.models';
import { CreateProject, ProjectsState } from '../../project.state';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { Router, RouterModule } from '@angular/router';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { HeaderService } from 'src/app/shared/services/header.service';
import { Observable } from 'rxjs';
import { User } from 'src/app/core/models/user.model';
import { EDSearchComponent } from 'src/app/shared/components/ed-search/ed-search.component';
import {
    dateFormatValidator,
    EdDatepickerComponent,
} from 'src/app/shared/components/forms/ed-datepicker/ed-datepicker.component';

const DEFAULT_SOLD_DAY_RATE = 850;

@UntilDestroy()
@Component({
    selector: 'project-create',
    templateUrl: './project-create.component.html',
    styleUrls: ['./project-create.component.scss'],
    standalone: true,
    schemas: [],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        SharedModule,
        TextfieldModule,
        ButtonModule,
        NumberfieldModule,
        RouterModule,
        EDSearchComponent,
        EdDatepickerComponent,
    ],
})
export class ProjectCreateComponent {
    @Select(ProjectsState.activities)
    public activities$!: Observable<BusinessActivityModel[]>;
    public activityList: { value: string; label: string }[] = [];

    public statusList = Object.values(ProjectStatus).map((value) => ({ value, label: value }));
    public buttonColors = ButtonColors;

    @Select(ProjectsState.leadUsers)
    public leadUsers$!: Observable<User[]>;
    public leadUserList: { value: string; label: string }[] = [];

    public projectFormGroup: FormGroup = this.formBuilder.group({
        code: [''],
        label: [''],
        soldAmount: [0],
        subcontractingAmount: [0],
        status: [ProjectStatus.ACTIVE],
        soldDayRate: [DEFAULT_SOLD_DAY_RATE],
        estimatedCloseDate: [undefined, dateFormatValidator()],
        activityId: [],
        leadUserId: [],
    });

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly router: Router,
        private readonly store: Store,
        private readonly alertService: AlertService,
        private readonly sidenavService: SidenavService,
        private readonly headerService: HeaderService,
    ) {
        this.activities$
            .pipe(untilDestroyed(this))
            .subscribe((activities: BusinessActivityModel[]) => {
                this.activityList = activities.map((a) => ({ value: a.id, label: a.label }));
            });
        this.leadUsers$.pipe(untilDestroyed(this)).subscribe((leadUsers: User[]) => {
            this.leadUserList = leadUsers.map((a) => ({
                value: a.id,
                label: `${a.firstname} ${a.lastname}`,
            }));
        });

        this.headerService.setBreadcrumbs([
            { link: '/main/projects/all', label: 'Projets' },
            { label: 'Nouveau projet' },
        ]);
        this.sidenavService.resetSubMenuItem('Projets', [
            {
                label: 'Tous les projets',
                icon: 'bi bi-folder2-open',
                routerLink: ['/main/projects'],
                routerLinkActiveOptions: { exact: true },
                hidden: false,
                isActive: false,
                subItems: [],
            },
            {
                label: 'Nouveau',
                icon: 'bi bi-file-earmark-plus',
                routerLink: ['/main/projects/new'],
                routerLinkActiveOptions: { exact: true },
                hidden: false,
                isActive: false,
                subItems: [],
            },
        ]);
        this.projectFormGroup = this.formBuilder.group({
            code: [''],
            label: [''],
            soldAmount: [0],
            subcontractingAmount: [0],
            status: [ProjectStatus.ACTIVE],
            soldDayRate: [DEFAULT_SOLD_DAY_RATE],
            estimatedCloseDate: [],
            activityId: [],
            leadUserId: [],
        });
    }

    public valid(): void {
        const project: any = {
            code: this.projectFormGroup.value.code,
            label: this.projectFormGroup.value.label,
            soldAmount: this.projectFormGroup.value.soldAmount,
            subcontractingAmount: this.projectFormGroup.value.subcontractingAmount,
            status: this.projectFormGroup.value.status,
            soldDayRate: this.projectFormGroup.value.soldDayRate,
            estimatedCloseDate: this.projectFormGroup.value.estimatedCloseDate,
            activityId: this.projectFormGroup.value.activityId,
            leadUserId: this.projectFormGroup.value.leadUserId,
        };
        //create
        this.store.dispatch(new CreateProject({ project })).subscribe({
            next: () => {
                this.alertService.valid('Création', `Projet "${project.label} "créé !`);
                this.close();
            },
            error: (err: any) => {
                console.info(err.error.message);
                this.alertService.error('Création', err.error.message);
            },
        });
    }

    public close() {
        this.router.navigate([`/main/projects/all`]);
    }

    public canCreateEdit(): boolean {
        return this.projectFormGroup.invalid || !this.projectFormGroup.dirty;
    }
}
